import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import bambooO from "../assets/bamboo-o.png";

function NavLogo() {
  return (
    <div className="flex items-center">
      <div className="flex items-center" style={{ letterSpacing: '0', lineHeight: 1 }}>
        <span 
          className="text-xl md:text-3xl text-[#E8E8E8] font-bold"
          style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.01em' }}
        >PR</span>
        <img 
          src={bambooO} 
          alt="O" 
          className="h-6 md:h-8 w-auto inline-block"
          style={{ margin: '0 -3px', verticalAlign: 'middle' }}
        />
        <span 
          className="text-xl md:text-3xl text-[#E8E8E8] font-bold"
          style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.01em' }}
        >BWA</span>
      </div>
      <div className="hidden sm:flex items-center ml-3 pl-3 border-l border-[#8BC34A]/40">
        <div className="flex flex-col">
          <span className="text-[#8BC34A] text-[10px] tracking-[0.1em] font-medium leading-tight">ECO BOIS</span>
          <span className="text-[#8BC34A] text-[10px] tracking-[0.1em] font-medium leading-tight">CONCEPT</span>
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = useMemo(() => [
    { name: "Accueil", href: "/" },
    { name: "Le Bambou", href: "/le-bambou" },
    { name: "Nos Produits", href: "/nos-produits" },
    { name: "Calculateur", href: "/calculateur" },
    { name: "Demande de devis", href: "/demande-de-devis" },
  ], []);

  const mobileNavLinks = useMemo(
    () => navLinks.filter(link => link.href !== "/demande-de-devis"),
    [navLinks]
  );

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "backdrop-blur-xl bg-[#2D2D2D]/95 border-b border-[#8BC34A]/20 shadow-lg"
            : "bg-[#2D2D2D]/60 backdrop-blur-sm"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center cursor-pointer shrink-0" data-testid="logo">
              <NavLogo />
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors duration-300 relative group ${
                    location.pathname === link.href
                      ? "text-[#8BC34A]"
                      : "text-[#E8E8E8] hover:text-[#8BC34A]"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#8BC34A] transition-all duration-300 ${
                    location.pathname === link.href ? "w-full" : "w-0 group-hover:w-full"
                  }`} />
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <Link
                to="/demande-de-devis"
                className="inline-flex text-[#2D2D2D] rounded-full px-3 py-1.5 md:px-6 md:py-3 text-[10px] md:text-sm font-bold tracking-wide hover:scale-105 transition-all duration-300 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(145deg, #e0e0e0, #a8a8a8)',
                  boxShadow: 'inset 0 0 0 2px #8BC34A, inset 0 1px 3px rgba(255,255,255,0.6), inset 0 -1px 3px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.3)',
                  border: '2px solid #b0b0b0',
                }}
                data-testid="contact-cta"
              >
                Devis
                <span className="hidden md:inline">&nbsp;gratuit</span>
              </Link>

              <motion.button
                className="md:hidden p-1.5 text-[#8BC34A]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.9 }}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#2D2D2D] pt-20"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-6 p-6">
              {mobileNavLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`text-2xl font-medium ${
                      location.pathname === link.href ? "text-[#8BC34A]" : "text-[#E8E8E8]"
                    }`}
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/demande-de-devis"
                  className="mt-2 bg-[#8BC34A] text-[#2D2D2D] rounded-full px-6 py-3 text-base font-semibold"
                >
                  Demander un devis
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
